import { ApiFailure, type ApiError } from './api';

/**
 * Service Admin - opérations admin-only.
 *
 * V1 : un seul endpoint, l'export CSV brut prospects + opportunités.
 *
 * Spécificité : `exportData` ne passe PAS par le wrapper `api.post` standard
 * car la response est un binaire (ZIP). On fait un fetch direct et on
 * retourne un Blob que le composant transforme en download via Object URL.
 */

export interface ExportDataResult {
  blob: Blob;
  filename: string;
}

function extractFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = /filename="([^"]+)"/.exec(contentDisposition);
  return match?.[1] ?? fallback;
}

export const adminApi = {
  /**
   * Déclenche l'export CSV admin et retourne un Blob ZIP.
   *
   * Le serveur applique un double-facteur soft : le code TOTP doit être
   * fourni et valide. Erreurs possibles :
   *   - 400 si totpCode malformé
   *   - 401 si TOTP invalide ou MFA non configuré
   *   - 403 si l'utilisateur n'est pas admin
   *   - 500 si le streaming échoue côté serveur
   *
   * Toutes les erreurs sont remontées en `ApiFailure` pour un traitement
   * homogène côté UI.
   */
  exportData: async (token: string, totpCode: string): Promise<ExportDataResult> => {
    const res = await fetch('/api/v1/admin/export/data', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ totpCode }),
    });

    if (!res.ok) {
      const text = await res.text();
      let body: ApiError;
      try {
        body = (JSON.parse(text) as { error?: ApiError })?.error ?? {
          code: 'INTERNAL_ERROR',
          message: 'Erreur',
        };
      } catch {
        body = { code: 'INTERNAL_ERROR', message: text || 'Erreur' };
      }
      throw new ApiFailure(res.status, body);
    }

    const blob = await res.blob();
    const filename = extractFilename(res.headers.get('content-disposition'), 'export-crm.zip');
    return { blob, filename };
  },
};
